import React, { useState } from 'react';
import Icon from '../../Icon';
import { C } from '../../../utils/styles';
import StyledSelect from '../../StyledSelect';
import { FORMAT_LABELS } from '../../../utils/censusFormatDetection';

const CONFIDENCE_BADGE = {
  high: { color: C.green, bg: C.greenLight, label: 'High confidence' },
  medium: { color: '#0284c7', bg: '#e0f2fe', label: 'Medium confidence' },
  low: { color: C.orange, bg: C.orangeLight, label: 'Low confidence' },
  none: { color: C.red, bg: C.redLight || '#fee2e2', label: 'Unknown format' },
};

export default function FormatDetectionCard({
  detection,
  sheet,
  fileName,
  onConfirm,
  onOverride,
  onBack,
}) {
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [chosen, setChosen] = useState(detection?.format || 'generic');
  const badge = CONFIDENCE_BADGE[detection?.confidence] || CONFIDENCE_BADGE.none;
  const label = FORMAT_LABELS[detection?.format] || 'Unknown format';

  const formatOptions = Object.entries(FORMAT_LABELS)
    .filter(([k]) => k !== 'unknown')
    .map(([value, label]) => ({ value, label }));

  return (
    <div data-testid="smart-upload-format-detected" style={{
      background: '#fff',
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      padding: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'start', gap: 16, marginBottom: 16 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 8,
          background: badge.bg, color: badge.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon name={detection?.format === 'unknown' ? 'help-circle-outline' : 'checkmark-circle'} style={{ fontSize: 22 }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>
            We detected your file as:
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }} data-testid="smart-upload-format-label">
            {label}
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 999,
            background: badge.bg, color: badge.color,
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
          }}>
            {badge.label}
          </div>
        </div>
      </div>

      <div style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
        fontSize: 13,
        color: C.textMid,
        lineHeight: 1.5,
      }}>
        <div style={{ fontWeight: 600, color: C.text, marginBottom: 6 }}>
          {fileName} · {sheet?.meta?.dataRowCount || 0} data rows · {sheet?.headers?.length || 0} columns · headers on row {(sheet?.headerRowIndex || 0) + 1}
        </div>
        {detection?.reasons?.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'start', gap: 6 }}>
            <Icon name="checkmark-outline" style={{ fontSize: 14, color: C.green, marginTop: 2, flexShrink: 0 }} />
            <span>{r}</span>
          </div>
        ))}
      </div>

      {!overrideOpen ? (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            data-testid="smart-upload-confirm-format"
            onClick={() => onConfirm(detection?.format || 'generic')}
            style={{
              padding: '10px 20px',
              background: C.green, color: '#fff',
              border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <Icon name="checkmark-circle" style={{ fontSize: 16 }} />
            Looks right, continue
          </button>
          <button
            data-testid="smart-upload-override-toggle"
            onClick={() => setOverrideOpen(true)}
            style={{
              padding: '10px 20px', background: 'transparent',
              color: C.text, border: `1px solid ${C.border}`,
              borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <Icon name="swap-horizontal-outline" style={{ fontSize: 16 }} />
            It's something else
          </button>
          {onBack && (
            <button
              data-testid="smart-upload-back"
              onClick={onBack}
              style={{
                padding: '10px 20px', background: 'transparent',
                color: C.textMid, border: 'none',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Use a different file
            </button>
          )}
        </div>
      ) : (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 8 }}>
            Choose the correct format:
          </div>
          <div style={{ marginBottom: 12 }}>
            <StyledSelect
              value={chosen}
              onValueChange={setChosen}
              options={formatOptions}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => onOverride(chosen)}
              data-testid="smart-upload-override-confirm"
              style={{
                padding: '10px 20px', background: C.blue, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Use this format
            </button>
            <button
              onClick={() => setOverrideOpen(false)}
              style={{
                padding: '10px 20px', background: 'transparent',
                color: C.textMid, border: `1px solid ${C.border}`,
                borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
